export type SessionLease<TAuthority extends object> = Readonly<{
  epoch: number;
  authority: TAuthority;
}>;

export class StaleSessionOperationError extends Error {
  constructor() {
    super("The private session operation belongs to a stale session epoch.");
    this.name = "StaleSessionOperationError";
  }
}

/**
 * Synchronous, render-owned boundary for private Shell writes.
 *
 * A lease is valid only while both the session epoch and its exact API authority
 * remain current. Invalidation happens before React's asynchronous purge so a
 * response already in flight cannot write during the render gap.
 */
export class SessionEpoch<TAuthority extends object> {
  private epoch = 0;
  private authority: TAuthority | null = null;
  private active = false;

  sync(active: boolean, authority: TAuthority): void {
    if (!active) {
      if (this.active || this.authority !== null) this.invalidate();
      return;
    }
    if (!this.active || this.authority !== authority) {
      this.epoch += 1;
      this.authority = authority;
      this.active = true;
    }
  }

  invalidate(expectedAuthority?: TAuthority): boolean {
    if (expectedAuthority && this.authority !== expectedAuthority) return false;
    if (!this.active && this.authority === null) return false;
    this.epoch += 1;
    this.authority = null;
    this.active = false;
    return true;
  }

  capture(authority: TAuthority): SessionLease<TAuthority> {
    return Object.freeze({ epoch: this.epoch, authority });
  }

  isCurrent(lease: SessionLease<TAuthority>): boolean {
    return this.active
      && this.epoch === lease.epoch
      && this.authority === lease.authority;
  }

  assertCurrent(lease: SessionLease<TAuthority>): void {
    if (!this.isCurrent(lease)) throw new StaleSessionOperationError();
  }
}
